import { FC } from "react"

import { ReactComponent as IconBolt } from "@common_assets/svg/bolt-solid.svg"
import { ReactComponent as IconCoin } from "@common_assets/svg/bonus-coin.svg"
import { ReactComponent as IconCalendar } from "@common_assets/svg/calendar-clock-solid.svg"
import { Box, Stack, Typography, useTheme } from "@mui/material"
import { useTranslation } from "react-i18next"

const DOT_COL = 20

const AttendanceUpsellView: FC = () => {
  const { t } = useTranslation("adminDashboard")
  const theme = useTheme()

  const steps = [
    { key: "stepAttendance", Icon: IconCalendar, meta: t("stepAttendanceMeta"), accent: false },
    { key: "stepSync",       Icon: IconBolt,     meta: t("stepSyncMeta"),       accent: false },
    { key: "stepBonus",      Icon: IconCoin,     meta: t("stepBonusMeta"),      accent: true  },
  ] as const

  return (
    <Box
      sx={{
        maxWidth: 480,
        mx: "auto",
        mt: { xs: 4, tablet: 8 },
        px: { xs: 2, tablet: 0 },
      }}
    >
      <Box sx={{ mb: 5 }}>
        {steps.map(({ key, Icon, meta, accent }, index) => (
          <Box key={key}>
            <Stack direction="row" alignItems="center" gap={2}>
              <Box
                sx={{
                  width: DOT_COL,
                  display: "flex",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Box
                  sx={{
                    width: accent ? 14 : 10,
                    height: accent ? 14 : 10,
                    borderRadius: "50%",
                    backgroundColor: accent
                      ? theme.palette.mockup.success50
                      : theme.palette.mockup.primary80,
                    boxShadow: accent
                      ? `0 0 0 4px ${theme.palette.mockup.successLight}`
                      : "none",
                  }}
                />
              </Box>

              <Stack
                direction="row"
                alignItems="center"
                gap={1.5}
                sx={{
                  flex: 1,
                  py: 1.5,
                  px: 2,
                  borderRadius: 2,
                  backgroundColor: accent
                    ? theme.palette.mockup.successLight
                    : "transparent",
                  border: "1px solid transparent",
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 1.5,
                    backgroundColor: accent
                      ? theme.palette.mockup.success50
                      : theme.palette.mockup.primary90,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Icon
                    width={18}
                    height={18}
                    color={accent ? theme.palette.common.white : theme.palette.primary.main}
                  />
                </Box>
                <Box>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color={accent ? "mockup.success50" : "mockup.neutral10"}
                    sx={{ lineHeight: 1.3 }}
                  >
                    {t(key)}
                  </Typography>
                  <Typography
                    variant="body3"
                    fontWeight={400}
                    color="mockup.neutral30"
                    sx={{ lineHeight: 1.4 }}
                  >
                    {meta}
                  </Typography>
                </Box>
              </Stack>
            </Stack>

            {index < steps.length - 1 && (
              <Stack direction="row" gap={2}>
                <Box
                  sx={{
                    width: DOT_COL,
                    display: "flex",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Box
                    sx={{
                      width: 2,
                      height: 20,
                      background: `linear-gradient(to bottom, ${theme.palette.mockup.primary80}, ${theme.palette.mockup.primary90})`,
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }} />
              </Stack>
            )}
          </Box>
        ))}
      </Box>

      <Box sx={{ height: 1, backgroundColor: theme.palette.mockup.primary90, mb: 3.5 }} />

      <Typography
        variant="body1"
        fontWeight={400}
        color="mockup.neutral20"
        sx={{ lineHeight: 1.75 }}
      >
        {t("attendanceUpsellMessage")}
      </Typography>
    </Box>
  )
}

export default AttendanceUpsellView
